import { useState, useEffect } from 'react';
import { Palette, Save, Loader2, CheckCircle } from 'lucide-react';
import Header from '../components/layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../services/api';
import type { Branding } from '../types';

export default function BrandingPage() {
  const { user, refreshUser } = useAuth();
  const [branding, setBranding] = useState<Partial<Branding>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user?.branding) {
      setBranding(user.branding);
    }
  }, [user]);

  const handleChange = (field: keyof Branding, value: string) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await userApi.updateBranding(branding);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save branding:', error);
      alert('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header
        title="Branding"
        subtitle="Customize your white-label reports"
      />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="card max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="text-teal" size={24} />
            <h3 className="text-lg font-tech font-semibold">Report Branding</h3>
          </div>

          <div className="space-y-6">
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={branding.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Your MSP Name"
                className="input-field w-full"
              />
            </div>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Logo URL
              </label>
              <input
                type="url"
                value={branding.logo_url || ''}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                className="input-field w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: PNG or SVG, max 200x80px
              </p>
            </div>

            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Primary Color
              </label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={branding.primary_color || '#008080'}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="w-12 h-10 rounded border border-teal/30 cursor-pointer"
                />
                <input
                  type="text"
                  value={branding.primary_color || '#008080'}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  placeholder="#008080"
                  className="input-field flex-1"
                />
              </div>
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={branding.contact_email || ''}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="contact@yourmsp.com"
                className="input-field w-full"
              />
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                value={branding.contact_phone || ''}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                placeholder="+49 123 456789"
                className="input-field w-full"
              />
            </div>

            {/* CTA Text */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Call-to-Action Text
              </label>
              <input
                type="text"
                value={branding.cta_text || ''}
                onChange={(e) => handleChange('cta_text', e.target.value)}
                placeholder="Schedule a Security Consultation"
                className="input-field w-full"
              />
            </div>

            {/* CTA URL */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Call-to-Action URL
              </label>
              <input
                type="url"
                value={branding.cta_url || ''}
                onChange={(e) => handleChange('cta_url', e.target.value)}
                placeholder="https://yourmsp.com/contact"
                className="input-field w-full"
              />
            </div>

            {/* Save Button */}
            <div className="pt-4 border-t border-teal/20">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : saved ? (
                  <CheckCircle size={18} />
                ) : (
                  <Save size={18} />
                )}
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Branding'}
              </button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="card max-w-2xl">
          <h3 className="text-lg font-tech font-semibold mb-4">Preview</h3>
          <div
            className="p-6 rounded-lg border-2"
            style={{ borderColor: branding.primary_color || '#008080' }}
          >
            <div className="flex items-center gap-4 mb-4">
              {branding.logo_url && (
                <img
                  src={branding.logo_url}
                  alt="Logo"
                  className="h-12 object-contain"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              )}
              <div>
                <h4
                  className="text-xl font-tech font-bold"
                  style={{ color: branding.primary_color || '#008080' }}
                >
                  {branding.company_name || 'Your Company'}
                </h4>
                <p className="text-sm text-gray-500">Security Assessment Report</p>
              </div>
            </div>
            <div className="text-sm text-gray-400 space-y-1">
              {branding.contact_email && <p>Email: {branding.contact_email}</p>}
              {branding.contact_phone && <p>Phone: {branding.contact_phone}</p>}
            </div>
            {branding.cta_text && (
              <button
                className="mt-4 px-4 py-2 rounded text-white text-sm"
                style={{ backgroundColor: branding.primary_color || '#008080' }}
              >
                {branding.cta_text}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
